import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import CompanyProfile from './CompanyProfile';
import './SupplierDashboard.css';

/* ─── SVG Icon Components (duotone, teal-aligned) ─── */

const IconLibrary = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z" opacity=".35" fill="currentColor"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);

const IconSubmit = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" opacity=".15" fill="currentColor"/>
    <path d="M12 8v8"/>
    <path d="M8 12h8"/>
  </svg>
);

const IconSubmissions = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" opacity=".15" fill="currentColor"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

const IconProfile = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" opacity=".15" fill="currentColor"/>
    <circle cx="12" cy="8" r="4"/>
    <path d="M20 21a8 8 0 1 0-16 0"/>
  </svg>
);

const IconDocs = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" opacity=".15" fill="currentColor"/>
    <path d="M3 9h18"/>
    <path d="M9 3v18"/>
    <path d="M13 13l2 2 4-4"/>
  </svg>
);

const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 4l4 4-4 4"/>
  </svg>
);


/* ─── Supplier Dashboard Component ─── */

const SupplierDashboard = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useContext(AuthContext);
  const [profileCompleted, setProfileCompleted] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─── Profile check (unchanged) ───
  useEffect(() => {
    const checkProfileCompletion = async () => {
      if (user?.uid) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserProfile(userData);

            const isCompleted = userData.profileCompleted || (
              userData.companyProfile?.companyName &&
              userData.companyProfile?.primaryContact &&
              userData.companyProfile?.phoneNumber &&
              userData.companyProfile?.email &&
              userData.companyProfile?.website
            );

            setProfileCompleted(isCompleted);

            if (!isCompleted) {
              setShowProfileModal(true);
            }
          }
        } catch (error) {
          console.error('Error checking profile completion:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    checkProfileCompletion();
  }, [user]);

  const handleProfileComplete = async (profileData) => {
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        companyProfile: {
          ...profileData,
          completedAt: new Date()
        },
        profileCompleted: true,
        companyName: profileData.companyName
      });

      setProfileCompleted(true);
      setShowProfileModal(false);
      setUserProfile(prev => ({
        ...prev,
        companyProfile: profileData,
        profileCompleted: true,
        companyName: profileData.companyName
      }));

      if (refreshUser) {
        await refreshUser();
      }

      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error saving profile. Please try again.');
    }
  };

  const handleCardClick = (path) => {
    if (!profileCompleted) {
      setShowProfileModal(true);
      return;
    }
    navigate(path);
  };

  // ─── Helpers ───
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
  const formattedTime = currentDate.toLocaleTimeString("en-US", {
    hour: "2-digit", minute: "2-digit",
  });

  // ─── Card data ───
  const cards = [
    { label: 'Material Library',   desc: 'Browse available materials and their properties',  path: '/categories',              Icon: IconLibrary,     color: 'teal',    locked: false,            isProfile: false },
    { label: 'Submit New Product', desc: 'Add new products for admins to review',             path: '/material-requests',       Icon: IconSubmit,      color: 'emerald', locked: !profileCompleted, isProfile: false },
    { label: 'My Submissions',    desc: 'View and track your material submissions',           path: '/supplier/my-submissions', Icon: IconSubmissions,  color: 'indigo',  locked: !profileCompleted, isProfile: false },
    { label: 'Company Profile',   desc: profileCompleted ? 'Update your company information' : 'Complete your company profile', path: null, Icon: IconProfile, color: 'amber', locked: false, isProfile: true },
    { label: 'Documentation',     desc: 'Upload and manage material documents',               path: '/supplier/documentation',  Icon: IconDocs,        color: 'violet',  locked: !profileCompleted, isProfile: false },
  ];

  const stats = [
    { value: 0, label: 'Total Submissions' },
    { value: 0, label: 'Approved' },
    { value: 0, label: 'Pending' },
    { value: 0, label: 'Under Review' },
  ];

  // ─── Loading ───
  if (loading) {
    return (
      <div className="neo-supplier-dashboard">
        <div className="sd-loading">
          <div className="sd-loading__spinner" />
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // ─── Render ───
  return (
    <div className="neo-supplier-dashboard">

      {/* ─── Header ─── */}
      <header className="sd-header">
        <div className="sd-header__inner">
          <div className="sd-header__text">
            <h1>Supplier Portal</h1>
            <p>Manage your material submissions and company profile</p>
          </div>
          <div className="sd-header__meta">
            <div className="sd-header__pill">
              <span>📅</span> {formattedDate} &middot; {formattedTime}
            </div>
            {userProfile?.companyProfile?.companyName && (
              <div className="sd-header__pill">
                <span>🏢</span> {userProfile.companyProfile.companyName}
              </div>
            )}
            <div className="sd-header__pill">
              <span>{profileCompleted ? '✅' : '⏳'}</span> {profileCompleted ? 'Profile Complete' : 'Profile Incomplete'}
            </div>
          </div>
        </div>

        {!profileCompleted && (
          <div className="sd-profile-banner">
            <span className="sd-profile-banner__icon">⚠️</span>
            <p className="sd-profile-banner__text">Complete your company profile to access all features</p>
          </div>
        )}
      </header>

      {/* ─── Tools section ─── */}
      <section className="sd-section">
        <div className="sd-section__header">
          <div className="sd-section__icon sd-section__icon--tools">📦</div>
          <h2 className="sd-section__title">Quick Access</h2>
          <span className="sd-section__count">{cards.length} tools</span>
        </div>

        <div className="sd-grid">
          {cards.map((card) => {
            const isLocked = card.locked;
            const isHighlight = card.isProfile && !profileCompleted;

            return (
              <div
                className={`sd-card ${isLocked ? 'sd-card--locked' : ''} ${isHighlight ? 'sd-card--highlight' : ''}`}
                key={card.label}
                onClick={() => {
                  if (card.isProfile) {
                    setShowProfileModal(true);
                  } else if (isLocked) {
                    setShowProfileModal(true);
                  } else {
                    handleCardClick(card.path);
                  }
                }}
                style={isLocked ? { pointerEvents: 'auto', cursor: 'pointer' } : undefined}
              >
                {/* Lock overlay */}
                {isLocked && (
                  <div className="sd-card__lock">
                    <span className="sd-card__lock-icon">🔒</span>
                    <span className="sd-card__lock-text">Complete profile first</span>
                  </div>
                )}

                <div className={`sd-card__icon sd-card__icon--${card.color}`}>
                  <card.Icon />
                </div>
                <div className="sd-card__body">
                  <h3>{card.label}</h3>
                  <p>{card.desc}</p>
                </div>
                <span className="sd-card__arrow"><IconArrow /></span>
              </div>
            );
          })}
        </div>
      </section>

      {/* ─── Stats section ─── */}
      <div className="sd-stats-panel">
        <div className="sd-stats-panel__header">
          <div className="sd-stats-panel__icon">📊</div>
          <h3 className="sd-stats-panel__title">Submission Statistics</h3>
        </div>
        <div className="sd-stats-grid">
          {stats.map((stat) => (
            <div className="sd-stat-block" key={stat.label}>
              <div className="sd-stat-block__value">{stat.value}</div>
              <div className="sd-stat-block__label">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Company Profile Modal (unchanged) ─── */}
      {showProfileModal && (
        <CompanyProfile
          isOpen={showProfileModal}
          onClose={() => !profileCompleted ? null : setShowProfileModal(false)}
          onSave={handleProfileComplete}
          existingProfile={userProfile?.companyProfile}
          isRequired={!profileCompleted}
        />
      )}
    </div>
  );
};

export default SupplierDashboard;
